import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";
import { PageType } from "@/types";

interface AuthContextType {
	user: any;
	token: string | null;
	login: (email: string, password: string) => Promise<void>;
	logout: () => void;
	updatePage: (pageId: string | number, page: Partial<PageType>) => Promise<unknown>;
	isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
	const [user, setUser] = useState<any>(null);
	const [token, setToken] = useState<string | null>(null);
	const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);

	useEffect(() => {
		// Prüfen, ob wir uns im Browser befinden
		if (typeof window !== "undefined") {
			const savedToken = localStorage.getItem("strapi_token");
			if (savedToken) {
				setToken(savedToken);
				setIsAuthenticated(true);
			}
		}
	}, []);

	const login = async (email: string, password: string) => {
		try {
			const response = await axios.post(`${process.env.NEXT_PUBLIC_API_ENDPOINT}/auth/local`, {
				identifier: email,
				password,
			});
			const jwt = response.data.jwt;
			setToken(jwt);
			setUser(response.data.user);
			setIsAuthenticated(true);
			if (typeof window !== "undefined") {
				localStorage.setItem("strapi_token", jwt); // Token nur im Browser speichern
			}
		} catch (error) {
			console.error("Login failed:", error);
			throw new Error("Anmeldung fehlgeschlagen");
		}
	};

	const logout = () => {
		setToken(null);
		setUser(null);
		setIsAuthenticated(false);
		if (typeof window !== "undefined") {
			localStorage.removeItem("strapi_token"); // Token nur im Browser entfernen
		}
	};

	const updatePage = async (pageId: string | number, pageUpdates: Partial<PageType>) => {
		if (!token) {
			Promise.reject();
		}

		try {
			const response = await axios.put(
				`${process.env.NEXT_PUBLIC_API_ENDPOINT}/pages/${pageId}`,
				{
					data: pageUpdates,
				},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				}
			);

			return response;
		} catch (e) {
			throw e;
		}
	};

	const value = {
		user,
		token,
		login,
		logout,
		updatePage,
		isAuthenticated,
	};

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
	const context = useContext(AuthContext);
	if (!context) {
		throw new Error("useAuth must be used within an AuthProvider");
	}
	return context;
};
